/**
 * The two requests this page makes. Both turn every failure into a message
 * key the page can translate, so no raw error ever reaches the guest.
 */
import axios from "axios";
import customAxios from "../../../utils/customAxios";

const MEET_ASSIST_PRICE_URL = "/api/admin/sitesetting/get-meet-assist-price";
const INQUIRY_URL = "/api/meet-assist-inquiry";

/** Sending email can be slow; give it longer than customAxios's 10s default. */
const SUBMIT_TIMEOUT_MS = 30000;

/** True when the request was cancelled on purpose (e.g. the page closed). */
export const isRequestCancelled = (error) => axios.isCancel(error);

/** A fresh cancel handle for one request: `{ token, cancel }`. */
export const createCancelSource = () => axios.CancelToken.source();

const PRICE_FIELDS = ["meetAssistBasePrice", "vipMeetAssistBasePrice", "vipMeetAssistAdditionalPrice"];

/** Error carrying a translation key for the guest and, for 400s, field errors. */
export class InquiryRequestError extends Error {
  constructor(messageKey, { status, fieldErrors } = {}) {
    super(messageKey);
    this.name = "InquiryRequestError";
    this.messageKey = messageKey;
    this.status = status;
    this.fieldErrors = fieldErrors || {};
  }
}

/**
 * Keeps only prices that are real positive numbers. A setting left at 0 or
 * missing falls back to the default instead of showing "0 JPY".
 */
const pickValidPrices = (responseData) =>
  PRICE_FIELDS.reduce((validPrices, fieldName) => {
    const price = Number(responseData && responseData[fieldName]);
    if (Number.isFinite(price) && price > 0) validPrices[fieldName] = price;
    return validPrices;
  }, {});

/**
 * Loads the Meet & Assist prices set in admin Site Settings.
 * @param {CancelToken} cancelToken - cancels the request when the page unmounts
 *   (axios 0.21 here has no AbortSignal support, so we use its CancelToken)
 * @returns {Promise<Object>} only the prices that came back valid
 */
export const fetchMeetAssistPrices = async (cancelToken) => {
  const response = await customAxios.get(MEET_ASSIST_PRICE_URL, { cancelToken });
  return pickValidPrices(response.data);
};

/** Maps an axios failure to what the guest should be told. */
const toInquiryRequestError = (error) => {
  if (axios.isCancel(error)) return new InquiryRequestError("Errors.Cancelled");

  if (error.code === "ECONNABORTED") return new InquiryRequestError("Errors.Timeout");

  // No response at all: offline, DNS, server down.
  if (!error.response) return new InquiryRequestError("Errors.Network");

  const { status, data } = error.response;
  if (status === 400) {
    return new InquiryRequestError("Errors.CheckFields", { status, fieldErrors: data && data.fieldErrors });
  }
  if (status === 429) return new InquiryRequestError("Errors.TooManyRequests", { status });
  return new InquiryRequestError("Errors.Server", { status });
};

/**
 * Sends the inquiry.
 * @param {Object} payload - from buildInquiryPayload()
 * @param {CancelToken} cancelToken
 * @throws {InquiryRequestError}
 */
export const submitMeetAssistInquiry = async (payload, cancelToken) => {
  try {
    const response = await customAxios.post(INQUIRY_URL, payload, { cancelToken, timeout: SUBMIT_TIMEOUT_MS });

    // Never assume success from a 2xx alone -- check the body says so.
    if (!response.data || response.data.status !== "success") {
      throw new InquiryRequestError("Errors.Server", { status: response.status });
    }
    return response.data.data;
  } catch (error) {
    if (error instanceof InquiryRequestError) throw error;
    throw toInquiryRequestError(error);
  }
};
